import { Component, Inject, OnInit, Optional } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import Swal from 'sweetalert2';
import {
  CreateDataUpdateRequestPayload,
  DEFAULT_REQUEST_MOTIVO,
  RequestableField,
  UserForRequest,
} from '../../../interfaces/data-update-request.interface';
import { DataUpdateRequestService } from '../../../services/data-update-request.service';
import { S3UrlService } from 'src/app/shared/services/s3-url.service';
import {
  FILE_FIELDS,
  formatDateForDisplay,
  humanizeFieldKey,
  hydrateCurrentDocumentUrls,
  isDateFieldKey,
  pairedFieldOf,
  parseUsuarioId,
} from '../../data-update-request/data-update-request.utils';

/**
 * Data inyectable opcional al abrir el modal. Si el operador llega
 * desde `compliance-user` (donde ya tiene un usuario en pantalla)
 * pasamos el `UserForRequest` precargado y saltamos el paso de
 * búsqueda. Si no viene, el modal arranca con el flujo de búsqueda
 * normal.
 */
export interface CreateDataUpdateRequestData {
  initialUser?: UserForRequest;
}

/**
 * Catálogo por defecto de campos que el operador puede solicitar
 * actualizar. Se omiten intencionalmente:
 *  - `email`: el cambio de correo va por su propio flujo (Cognito).
 *  - `nombres`, `apellidos`, `numeroId`: son datos de identidad core,
 *    sólo se modifican vía OCR + verificación facial — no por un form
 *    simple. Permitirle al operador solicitarlos por esta vía induciría
 *    inconsistencias entre el documento físico y la BD.
 *  - `fechaExp` (vencimiento del documento): se deriva del OCR al
 *    re-subir la foto frontal — pedirla por separado invita a
 *    inconsistencias con el documento físico.
 *  - `fechaNacimiento`: también la deriva el OCR, y es la que decide la
 *    edad mínima (18 años); el backend no la acepta como campo suelto.
 *
 * Quedan los campos "blandos" (contacto, residencia, datos de empresa)
 * y los archivos de identidad para re-upload con OCR.
 */
const FALLBACK_FIELDS: RequestableField[] = [
  { fieldKey: 'nacionalidad', label: 'Nacionalidad', fieldType: 'text', group: 'personal' },
  { fieldKey: 'direccion', label: 'Dirección', fieldType: 'text', group: 'personal' },
  { fieldKey: 'ciudadResidencia', label: 'Ciudad de residencia', fieldType: 'text', group: 'personal' },
  { fieldKey: 'paisResidencia', label: 'País de residencia', fieldType: 'text', group: 'personal' },
  { fieldKey: 'prefijoTelefono', label: 'Prefijo telefónico', fieldType: 'phonePrefix', group: 'personal' },
  { fieldKey: 'telefono', label: 'Teléfono', fieldType: 'text', group: 'personal' },
  { fieldKey: 'fotoIdfrontal', label: 'Foto frontal del documento', fieldType: 'file', group: 'document', requiresCompliance: true },
  { fieldKey: 'fotoIdPosterior', label: 'Foto reverso del documento', fieldType: 'file', group: 'document', requiresCompliance: true },
  { fieldKey: 'selfie', label: 'Selfie del rostro', fieldType: 'file', group: 'document', requiresCompliance: true },
  { fieldKey: 'razonSocial', label: 'Razón social', fieldType: 'text', group: 'company', companyOnly: true },
  { fieldKey: 'rutEmpresa', label: 'RUT empresa', fieldType: 'text', group: 'company', companyOnly: true },
  { fieldKey: 'nombreComercial', label: 'Nombre comercial', fieldType: 'text', group: 'company', companyOnly: true },
];

@Component({
  selector: 'app-create-data-update-request',
  templateUrl: './create-data-update-request.component.html',
  styleUrls: ['./create-data-update-request.component.sass'],
})
export class CreateDataUpdateRequestComponent implements OnInit {
  // Búsqueda de usuario
  searchQuery: string = '';
  searching: boolean = false;
  searchResults: UserForRequest[] = [];
  selectedUser: UserForRequest | null = null;

  // Catálogo de campos
  allFields: RequestableField[] = [];
  selectedFieldKeys = new Set<string>();

  // Form
  deadlineAt: string = this.defaultDeadline();
  /**
   * Motivo pre-rellenado con el texto amigable del plan (§2.1). El
   * operador puede editarlo libremente antes de enviar — el front no
   * fuerza el default si lo cambian.
   */
  motivo: string = DEFAULT_REQUEST_MOTIVO;

  saving: boolean = false;
  errorMessage: string | null = null;

  constructor(
    private dialogRef: MatDialogRef<CreateDataUpdateRequestComponent>,
    private service: DataUpdateRequestService,
    private s3UrlService: S3UrlService,
    @Optional()
    @Inject(MAT_DIALOG_DATA)
    private data?: CreateDataUpdateRequestData,
  ) {}

  ngOnInit(): void {
    this.loadFields();
    // Si el modal se abrió con un usuario inicial (desde compliance-user),
    // saltamos directo al paso 2 sin pedir búsqueda.
    if (this.data?.initialUser) {
      this.selectedUser = this.data.initialUser;
      // Firma las URLs de documentos del usuario inicial en background.
      hydrateCurrentDocumentUrls(this.selectedUser, this.s3UrlService);
    }
  }

  private async loadFields(): Promise<void> {
    try {
      const fields = await this.service.getRequestableFields();
      const source = Array.isArray(fields) && fields.length ? fields : FALLBACK_FIELDS;
      // `fechaExp` se deriva del OCR del documento; no se ofrece como campo
      // solicitable aunque el backend siga devolviéndolo desde /fields.
      this.allFields = source.filter((f) => f.fieldKey !== 'fechaExp');
    } catch {
      this.allFields = FALLBACK_FIELDS;
    }
  }

  async search(): Promise<void> {
    const q = this.searchQuery.trim();
    if (!q) return;
    this.searching = true;
    try {
      // Si el operador escribió un número puro, lo interpretamos como
      // `usuarioId` (búsqueda exacta) — caso de uso principal. Si no,
      // mandamos `q` para búsqueda libre por nombre / numero_id / email.
      const filters = this.buildSearchFilters(q);
      const res = await this.service.listUsersForRequest({
        ...filters,
        limit: 20,
      });
      this.searchResults = res.data;
    } catch (err: any) {
      this.searchResults = [];
      this.errorMessage = err?.error?.message || 'No se pudo buscar usuarios.';
    } finally {
      this.searching = false;
    }
  }

  /**
   * Decide qué filtro mandar al backend según lo que escribió el operador.
   * Un input puramente numérico se interpreta como `usuarioId` (búsqueda
   * exacta por ID), cualquier otra cosa como `q` (búsqueda libre).
   */
  private buildSearchFilters(input: string): { usuarioId?: number; q?: string } {
    const usuarioId = parseUsuarioId(input);
    if (usuarioId !== null) return { usuarioId };
    return { q: input };
  }

  selectUser(u: UserForRequest): void {
    this.selectedUser = u;
    this.searchResults = [];
    // Firma URLs de documentos en background — la vista previa aparece
    // unos ms después; mientras tanto el placeholder cubre el hueco.
    hydrateCurrentDocumentUrls(this.selectedUser, this.s3UrlService);
  }

  changeUser(): void {
    this.selectedUser = null;
    this.selectedFieldKeys.clear();
  }

  get visibleFields(): RequestableField[] {
    if (!this.selectedUser) return [];
    const isCompany = this.isCompany(this.selectedUser);
    const { active: hasCompliance } = this.resolveCompliance(this.selectedUser);
    return this.allFields.filter((f) => {
      if (f.companyOnly && !isCompany) return false;
      if (f.requiresCompliance && !hasCompliance) return false;
      return true;
    });
  }

  /**
   * Acepta tanto `tipoUsuarioId` (camelCase) como `tipoUsuarioID` (legacy)
   * — distintos endpoints del proyecto usan uno u otro.
   */
  private isCompany(u: UserForRequest): boolean {
    if (typeof u.tipoUsuarioId === 'number') return u.tipoUsuarioId === 1;
    if (typeof u.tipoUsuarioID === 'number') return u.tipoUsuarioID === 1;
    return false;
  }

  /**
   * ¿Se le pueden pedir documentos? Detecta el estado de compliance del
   * usuario aceptando los varios shapes que devuelven los distintos
   * endpoints del proyecto:
   *  - `estadoComplianceId` (number, nuevo).
   *  - `estadoComplianceID` (legacy CamelCase) — idem.
   *  - `estadoCompliance.estadoID` (objeto anidado).
   *  - `estadoCompliance` (string label `'APROBADO' | 'BLOQUEADO' | …`).
   *
   * `active` es true para aprobado (3), bloqueado (4, opera como aprobado)
   * y por aprobar (0): a ellos se les pueden pedir documentos. A rechazados
   * (1) y blacklist (2), no. Igual que el backend, que lo vuelve a validar.
   *
   * Devuelve `{ known: false }` cuando ninguno de esos campos está
   * disponible — en ese caso asumimos activo (no bloqueamos al operador
   * por información incierta) y ocultamos el banner de advertencia.
   */
  private resolveCompliance(u: UserForRequest): { known: boolean; active: boolean } {
    const aceptaDocumentos = (id: number) => id === 0 || id === 3 || id === 4;
    if (typeof u.estadoComplianceId === 'number') {
      return { known: true, active: aceptaDocumentos(u.estadoComplianceId) };
    }
    if (typeof u.estadoComplianceID === 'number') {
      return { known: true, active: aceptaDocumentos(u.estadoComplianceID) };
    }
    const ec = u.estadoCompliance;
    if (typeof ec === 'string') {
      const s = ec.toUpperCase();
      return {
        known: true,
        active: ['APROBADO', 'ACTIVO', 'BLOQUEADO', 'SUSPENDIDO', 'POR APROBAR', 'PENDIENTE'].includes(s),
      };
    }
    if (ec && typeof ec === 'object' && typeof ec.estadoID === 'number') {
      return { known: true, active: aceptaDocumentos(ec.estadoID) };
    }
    return { known: false, active: true };
  }

  /**
   * Verdadero sólo cuando sabemos a ciencia cierta que el compliance NO
   * está aprobado. Si el estado es desconocido, no mostramos banner —
   * evitamos asustar al operador con falsos positivos.
   */
  get showComplianceWarning(): boolean {
    if (!this.selectedUser) return false;
    const r = this.resolveCompliance(this.selectedUser);
    return r.known && !r.active;
  }

  /**
   * Toggle del checkbox. Cuando el field es parte de un par obligatorio
   * (teléfono ↔ prefijo), aplicamos la misma acción a su par para que
   * siempre viajen juntos en la solicitud — pedir el número sin el
   * prefijo no tiene sentido, y viceversa.
   */
  toggleField(fieldKey: string): void {
    const nowSelected = !this.selectedFieldKeys.has(fieldKey);
    this.applyFieldSelection(fieldKey, nowSelected);
    const pair = pairedFieldOf(fieldKey);
    if (pair && this.isPairFieldVisible(pair)) {
      this.applyFieldSelection(pair, nowSelected);
    }
  }

  private applyFieldSelection(fieldKey: string, selected: boolean): void {
    if (selected) {
      this.selectedFieldKeys.add(fieldKey);
    } else {
      this.selectedFieldKeys.delete(fieldKey);
    }
  }

  /**
   * Sólo aplicamos el sync del par si el field compañero está en la
   * lista de fields visibles para este usuario (respetamos empresa-only
   * y compliance gates). Evita tildar un field que el operador no puede
   * ver ni desmarcar después.
   */
  private isPairFieldVisible(fieldKey: string): boolean {
    return this.visibleFields.some((f) => f.fieldKey === fieldKey);
  }

  isFieldSelected(fieldKey: string): boolean {
    return this.selectedFieldKeys.has(fieldKey);
  }

  labelOfField(f: RequestableField): string {
    return f.label || humanizeFieldKey(f.fieldKey);
  }

  /** ¿Este fieldKey representa un archivo? (decide texto inline vs thumbnail) */
  isFileField(fieldKey: string): boolean {
    return FILE_FIELDS.has(fieldKey);
  }

  /**
   * Devuelve el valor actual del usuario para ese campo. Lee primero
   * de `currentValues` (sub-objeto opcional) y cae a leer el campo en
   * el root del usuario (caso real del endpoint `/users` que devuelve
   * `telefono`, `direccion`, etc. directo en el root).
   */
  currentValueOf(fieldKey: string): string | null {
    if (!this.selectedUser) return null;
    const fromMap = this.selectedUser.currentValues?.[fieldKey];
    const v =
      fromMap !== undefined && fromMap !== null
        ? fromMap
        : (this.selectedUser as any)[fieldKey];
    if (v === null || v === undefined || v === '') return null;
    if (typeof v === 'boolean') return v ? 'Sí' : 'No';
    if (isDateFieldKey(fieldKey) && typeof v === 'string') {
      const formatted = formatDateForDisplay(v);
      if (formatted) return formatted;
    }
    return String(v);
  }

  /**
   * URL pre-firmada GET del documento actual del usuario para
   * previsualizar en thumbnail. `null` mientras backend no exponga
   * `currentDocumentUrls` (ver §9 del plan); el template entonces
   * muestra un placeholder.
   */
  currentDocUrlOf(fieldKey: string): string | null {
    if (!this.selectedUser) return null;
    return this.selectedUser.currentDocumentUrls?.[fieldKey] ?? null;
  }

  // ── Lightbox local para previsualizar el documento actual ─────────

  lightboxUrl: string | null = null;

  isImageUrl(url: string | null | undefined): boolean {
    if (!url) return false;
    return /\.(jpe?g|png|gif|webp|bmp)(\?|$)/i.test(url);
  }

  previewDocument(url: string, event?: Event): void {
    event?.stopPropagation();
    event?.preventDefault();
    if (this.isImageUrl(url)) {
      this.lightboxUrl = url;
      document.body.style.overflow = 'hidden';
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  closeLightbox(): void {
    this.lightboxUrl = null;
    document.body.style.overflow = '';
  }

  async submit(): Promise<void> {
    if (!this.selectedUser) {
      this.errorMessage = 'Seleccioná un usuario.';
      return;
    }
    if (this.selectedFieldKeys.size === 0) {
      this.errorMessage = 'Seleccioná al menos un campo a solicitar.';
      return;
    }
    if (!this.deadlineAt) {
      this.errorMessage = 'Definí un plazo.';
      return;
    }
    if (!this.motivo.trim()) {
      this.errorMessage = 'Indicá un motivo.';
      return;
    }

    this.errorMessage = null;

    if (this.selectedUser.activeRequestId) {
      const confirm = await Swal.fire({
        icon: 'warning',
        title: 'Usuario con solicitud activa',
        text: 'Este usuario ya tiene una solicitud abierta. ¿Querés reemplazarla?',
        showCancelButton: true,
        confirmButtonText: 'Reemplazar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#1e5a3f',
      });
      if (!confirm.isConfirmed) return;
    }

    const payload: CreateDataUpdateRequestPayload = {
      usuarioId: this.selectedUser.usuarioId,
      deadlineAt: this.normalizeDeadline(this.deadlineAt),
      motivo: this.motivo.trim(),
      items: Array.from(this.selectedFieldKeys).map((fieldKey) => ({ fieldKey })),
    };

    this.saving = true;
    try {
      await this.service.createRequest(payload);
      this.dialogRef.close({ created: true });
    } catch (err: any) {
      this.errorMessage =
        err?.error?.message || err?.message || 'No se pudo crear.';
    } finally {
      this.saving = false;
    }
  }

  cancel(): void {
    this.dialogRef.close({ created: false });
  }

  private defaultDeadline(): string {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().slice(0, 10);
  }

  /**
   * Fecha de hoy en formato `YYYY-MM-DD` para bindear como `[min]` del
   * `<input type="date">`. Bloquea que el operador seleccione fechas
   * pasadas — el backend rechazaría con 400 y el UX quedaría opaco.
   */
  get todayIso(): string {
    return new Date().toISOString().slice(0, 10);
  }

  private normalizeDeadline(value: string): string {
    // El input type=date devuelve YYYY-MM-DD; el backend espera ISO con hora.
    // Tomamos las 23:59 del día elegido en zona local del operador.
    const d = new Date(`${value}T23:59:00`);
    return d.toISOString();
  }
}
